import { action, makeObservable, observable } from "mobx";
import BaseObjectStore, { BaseObjectStoreParams } from "../BaseObjectStore";
import { SignalsEndpoints } from "../../SignalsModels";
import type {
    ISignalsSmallCard,
    SignalDataEndpoint,
    SignalsFilters,
    SignalStatType,
} from "../../SignalsModels";
import { formatSmallCardResponse } from "../utils";
import { Props as TrendingProps } from "../SignalsTrendingIndicator";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";

const trendingPropsByEndpoint: Partial<
    Record<SignalDataEndpoint, Partial<TrendingProps>>
> = {
    [SignalsEndpoints.smallCards.AvgModuleScoreId]: { isNegativeRed: true },
    [SignalsEndpoints.smallCards.ShareOfConversationsId]: { isGrayOnly: true },
};

// Cards whose date range and date type are locked to the dashboard's.
const dateFiltersDisabledEndpoints: ReadonlySet<SignalDataEndpoint> = new Set([
    SignalsEndpoints.smallCards.ShareOfConversationsId,
]);

export type SmallCardState = {
    [key: string]: string | number | boolean;
};

export interface SmallCardRequestData {
    stat: number | null;
    change: number;
    type: SignalStatType;
    secondaryText?: string;
    /**
     * How many conversations the card's drilldown will return. Not derivable from `stat` when the
     * card shows a duration or a percentage, so the backend reports it separately.
     */
    conversationCount?: number;
}

export interface SmallCardFormattedData {
    stat?: string;
    unformattedStat?: number;
    secondaryText?: string;
    conversationCount?: number;
    trendingProps: TrendingProps;
}

// These are the properties on the stringified array from the backend
export interface AvailableOptionObjects {
    display: string;
    value: string | number | boolean;
    property?: string;
}

export type SmallCardOwnInfo = {
    isLoading?: boolean;
    data?: SmallCardFormattedData;
    state?: SmallCardState;
};

export type SmallCardInfo = ISignalsSmallCard & SmallCardOwnInfo;

class SmallCardStore extends BaseObjectStore<
    SmallCardFormattedData,
    SmallCardState
> {
    @observable availableOptionObjects?: string;

    constructor({
        baseObject,
        rootStore,
        options,
        initialData,
        refreshOnCreate,
    }: BaseObjectStoreParams<SmallCardFormattedData, ISignalsSmallCard>) {
        super({
            baseObject,
            rootStore,
            options,
            initialData,
            refreshOnCreate: false,
        });
        makeObservable(this);
        this.title = baseObject.title;
        this.availableOptionObjects = baseObject.availableOptionObjects;
        this.setDefaultStateFromOptions();
        if (refreshOnCreate) {
            // Initial data fetch, we have to cancel the base object one since we need to wait for the above state first
            this.runRefresh();
        }
    }

    private buildRequestPayload(state: SmallCardState = this.state || {}) {
        return {
            ...this.getFilters(),
            ...state,
            // Share of Conversations needs the dashboard-only filters for its denominator.
            ...(this.dataEndpoint ===
                SignalsEndpoints.smallCards.ShareOfConversationsId && {
                baselineFilters: this.getBaselineFiltersForRequest(),
            }),
        };
    }

    @action
    public async refreshForStateChange(updatedState: SmallCardState) {
        this.state = updatedState;
        await this.runRefresh();
    }

    protected override getCacheFiltersPart(): unknown {
        return this.getFilters();
    }

    protected override getCacheSelectionsPart(): unknown {
        return this.state ?? {};
    }

    public override get isDateFilterDisabled(): boolean {
        return (
            !!this.dataEndpoint &&
            dateFiltersDisabledEndpoints.has(this.dataEndpoint)
        );
    }

    protected override getCacheExtraParts(): unknown {
        return this.dataEndpoint ===
            SignalsEndpoints.smallCards.ShareOfConversationsId
            ? { baselineFilters: this.getBaselineFiltersForRequest() }
            : undefined;
    }

    public async getData({
        abortSignal,
    }: {
        abortSignal: AbortSignal;
    }): Promise<SmallCardFormattedData> {
        // Ensure we have a default state before requesting data
        if (
            !this.state ||
            this.state === undefined ||
            Object.keys(this.state).length === 0
        ) {
            this.setDefaultStateFromOptions();
        }
        if (!this.dataEndpoint) {
            throw new Error("Data endpoint is not defined for SmallCard");
        }

        const request = this.buildRequestPayload(this.state || {});
        const result = await this.signalsService.getSmallCardData(
            request,
            this.dataEndpoint,
            abortSignal,
        );
        const formattedResult = formatSmallCardResponse(result);
        return {
            ...formattedResult,
            trendingProps: {
                ...formattedResult.trendingProps,
                ...trendingPropsByEndpoint[this.dataEndpoint],
            },
        };
    }

    public getFilters(): SignalsFilters {
        const filters = this.getMergedFiltersForRequest();
        const startDate = (filters.startDate as string | undefined) ?? "";
        const endDate = (filters.endDate as string | undefined) ?? "";
        const dateReference =
            filters.dateReferenceOption ?? (filters as any).dateReference;

        return {
            ...filters,
            startDate: startDate ?? "",
            endDate: endDate ?? "",
            dateReference,
        };
    }

    @action
    public updateSmallCard(info: Partial<SmallCardInfo>) {
        if (info.state) {
            this.state = info.state;
        }
    }

    public async updateSmallCardConfiguration({
        title,
        objectFilters,
    }: {
        title?: string;
        objectFilters?: string | null;
    }): Promise<boolean> {
        try {
            // isEditable means either its a standard dashboard (you can edit those selections and such) OR you have edit permission. isSaveable means you have to have permission to edit.
            // BOTH are required to actually be able to save changes to the backend, otherwise we just apply them locally for the user's current session.
            if (this.isEditable && this.isSaveable) {
                await this.signalsService.updateSignalsSmallCard({
                    id: this.id,
                    title,
                    objectFilters,
                });
            }
            if (title !== undefined) {
                this.title = title;
            }

            return true;
        } catch (_error) {
            this.rootStore
                .getStore(MessageStore)
                .logError("Failed to update card settings.");
            return false;
        }
    }

    /**
     * Opens this card's point menu by selecting the card as a whole. Insight cards plot nothing, so
     * the selection carries the card's title and the conversation count behind its stat rather than
     * an x/y pair.
     */
    public selectCard() {
        if (!this.drilldownStore) return;
        this.drilldownStore.clearSelection();
        this.drilldownStore.selectSmallCard({
            title: this.title,
            count: this.data?.conversationCount,
        });
    }

    @action
    private setDefaultStateFromOptions() {
        if (this.state) return;
        try {
            const parsed: AvailableOptionObjects[] = JSON.parse(
                this.availableOptionObjects || "[]",
            );
            const first = parsed[0];
            // Cards without options still need a state: the drilldown store refuses to build
            // without one, and these cards are no less drillable for having nothing to select.
            this.state = first?.property
                ? { [first.property]: first.value }
                : {};
        } catch (error) {
            console.error(
                "Failed to parse SmallCard options for default state",
                error,
            );
            this.state = {};
        }
    }
}

export default SmallCardStore;
